import React, { useEffect, useMemo, useState } from 'react';

function initialFormData(user) {
  return {
    name: user?.name || '',
    mobile: user?.mobile || '',
    address: user?.address || '',
    age: user?.age ?? '',
    gender: user?.gender || 'N/A',
    center: user?.center || '',
    designation: user?.designation || '',
    organization: user?.organization || '',
    qualification: user?.qualification || '',
    telResidence: user?.telResidence || '',
    country: user?.country || '',
    attendingAtLocation: user?.attendingAtLocation === false ? false : true,
    tanzeemMember: user?.tanzeemMember === true,
    coordinator: user?.coordinator === true,
    admin: user?.admin === true,
  };
}

const UserEditForm = ({
  user,
  locationOptions,
  onClose,
  onSave,
  saving,
}) => {
  const [formData, setFormData] = useState(initialFormData(user));
  const [error, setError] = useState('');

  useEffect(() => {
    setFormData(initialFormData(user));
    setError('');
  }, [user]);

  const createdOnLabel = useMemo(() => {
    if (!user?.createdOnMs) return 'N/A';
    const parsed = new Date(user.createdOnMs);
    return Number.isNaN(parsed.getTime()) ? 'N/A' : parsed.toLocaleString();
  }, [user]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    const ageValue = formData.age === '' ? null : Number(formData.age);
    if (ageValue !== null && (!Number.isInteger(ageValue) || ageValue < 0 || ageValue > 120)) {
      setError('Age must be an integer between 0 and 120.');
      return;
    }

    try {
      await onSave({
        ...formData,
        age: ageValue,
      });
    } catch (saveErr) {
      setError(saveErr?.message || 'Failed to save user.');
    }
  };

  return (
    <div className="modal-overlay" onClick={saving ? undefined : onClose}>
      <div className="modal-content form-modal" onClick={(e) => e.stopPropagation()}>
        <div className="form-header">
          <h2>Edit User</h2>
          <button type="button" className="btn-close" onClick={onClose} disabled={saving}>
            ×
          </button>
        </div>

        {error ? <div className="form-error">{error}</div> : null}

        <form onSubmit={handleSubmit} className="location-form">
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="user-email">Email (Read-only)</label>
              <input id="user-email" type="text" value={user?.email || ''} disabled />
            </div>

            <div className="form-group">
              <label htmlFor="user-uid">UID (Read-only)</label>
              <input id="user-uid" type="text" value={user?.uid || 'N/A'} disabled />
            </div>

            <div className="form-group full-width">
              <label htmlFor="user-created">Created On (Read-only)</label>
              <input id="user-created" type="text" value={createdOnLabel} disabled />
            </div>

            <div className="form-section-title">Profile Fields</div>

            <div className="form-group">
              <label htmlFor="name">Name</label>
              <input id="name" name="name" type="text" value={formData.name} onChange={handleChange} />
            </div>

            <div className="form-group">
              <label htmlFor="mobile">Mobile</label>
              <input id="mobile" name="mobile" type="text" value={formData.mobile} onChange={handleChange} />
            </div>

            <div className="form-group full-width">
              <label htmlFor="address">Address</label>
              <input id="address" name="address" type="text" value={formData.address} onChange={handleChange} />
            </div>

            <div className="form-group">
              <label htmlFor="age">Age</label>
              <input id="age" name="age" type="number" min="0" max="120" value={formData.age} onChange={handleChange} />
            </div>

            <div className="form-group">
              <label htmlFor="gender">Gender</label>
              <select id="gender" name="gender" value={formData.gender} onChange={handleChange}>
                <option value="N/A">N/A</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="center">Center</label>
              <input
                id="center"
                name="center"
                type="text"
                list="user-edit-center-options"
                value={formData.center}
                onChange={handleChange}
              />
              <datalist id="user-edit-center-options">
                {locationOptions.map((location) => (
                  <option key={location} value={location} />
                ))}
              </datalist>
            </div>

            <div className="form-group">
              <label htmlFor="country">Country</label>
              <input id="country" name="country" type="text" value={formData.country} onChange={handleChange} />
            </div>

            <div className="form-group">
              <label htmlFor="designation">Designation</label>
              <input id="designation" name="designation" type="text" value={formData.designation} onChange={handleChange} />
            </div>

            <div className="form-group">
              <label htmlFor="organization">Organization</label>
              <input id="organization" name="organization" type="text" value={formData.organization} onChange={handleChange} />
            </div>

            <div className="form-group">
              <label htmlFor="qualification">Qualification</label>
              <input id="qualification" name="qualification" type="text" value={formData.qualification} onChange={handleChange} />
            </div>

            <div className="form-group">
              <label htmlFor="telResidence">Residence Tel.</label>
              <input id="telResidence" name="telResidence" type="text" value={formData.telResidence} onChange={handleChange} />
            </div>

            <div className="form-section-title">Flags</div>

            <div className="form-group checkbox-group">
              <label htmlFor="attendingAtLocation">
                <input
                  id="attendingAtLocation"
                  name="attendingAtLocation"
                  type="checkbox"
                  checked={formData.attendingAtLocation}
                  onChange={handleChange}
                />
                Attending at Location
              </label>
            </div>

            <div className="form-group checkbox-group">
              <label htmlFor="tanzeemMember">
                <input
                  id="tanzeemMember"
                  name="tanzeemMember"
                  type="checkbox"
                  checked={formData.tanzeemMember}
                  onChange={handleChange}
                />
                Tanzeem Member
              </label>
            </div>

            <div className="form-group checkbox-group">
              <label htmlFor="coordinator">
                <input
                  id="coordinator"
                  name="coordinator"
                  type="checkbox"
                  checked={formData.coordinator}
                  onChange={handleChange}
                />
                Coordinator
              </label>
            </div>

            <div className="form-group checkbox-group">
              <label htmlFor="admin">
                <input
                  id="admin"
                  name="admin"
                  type="checkbox"
                  checked={formData.admin}
                  onChange={handleChange}
                />
                Admin
              </label>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn-cancel" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="btn-save" disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserEditForm;
